/**
 * AdminAffiliatesPage — Beheer affiliate-aanmeldingen (P3).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Users, RefreshCw, Loader2, CheckCircle, X, Copy, Search, Filter,
} from 'lucide-react';

const STATUS_COLORS = {
  pending: 'text-amber-400 bg-amber-500/10',
  approved: 'text-emerald-400 bg-emerald-500/10',
  rejected: 'text-red-400 bg-red-500/10',
};

export default function AdminAffiliatesPage() {
  const { api } = useAuth();
  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [approving, setApproving] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api('get', `/api/admin/affiliates${statusFilter ? `?status=${statusFilter}` : ''}`);
      setAffiliates(r.data.affiliates || []);
    } catch {
      toast.error('Affiliates laden mislukt');
    }
    setLoading(false);
  }, [api, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = affiliates.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (a.full_name || '').toLowerCase().includes(q) ||
      (a.email || '').toLowerCase().includes(q) ||
      (a.company_name || '').toLowerCase().includes(q)
    );
  });

  const reject = async (id) => {
    if (!confirm('Aanmelding afwijzen?')) return;
    try {
      await api('post', `/api/admin/affiliates/${id}/reject`);
      toast.success('Afgewezen');
      load();
    } catch {
      toast.error('Mislukt');
    }
  };

  return (
    <div className="min-h-screen bg-[#0f172a] text-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
          <div>
            <p className="text-[#D4A017] text-xs uppercase tracking-wider font-bold mb-1">Admin</p>
            <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: "'Sora', sans-serif" }}>Affiliates</h1>
            <p className="text-white/60 text-sm mt-1">20% commissie van eerste maand · Externe aanmelding</p>
          </div>
          <button onClick={load} className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-lg px-3 py-2 text-sm flex items-center gap-1.5" data-testid="aff-refresh">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Totaal', value: affiliates.length, color: 'text-white/70' },
            { label: 'In review', value: affiliates.filter((a) => a.status === 'pending').length, color: 'text-amber-400' },
            { label: 'Actief', value: affiliates.filter((a) => a.status === 'approved').length, color: 'text-emerald-400' },
            { label: 'Afgewezen', value: affiliates.filter((a) => a.status === 'rejected').length, color: 'text-red-400' },
          ].map((s) => (
            <div key={s.label} className="bg-white/5 rounded-xl p-4 border border-white/5">
              <p className="text-white/50 text-xs uppercase tracking-wide font-semibold mb-1">{s.label}</p>
              <p className={`text-2xl font-bold ${s.color}`} style={{ fontFamily: "'Sora', sans-serif" }}>{s.value}</p>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2 mb-5 flex-wrap">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek op naam of e-mail" className="w-full bg-white/5 border border-white/10 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-white/40" data-testid="aff-search" />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white" data-testid="aff-status-filter">
            <option value="">Alle</option>
            <option value="pending">In review</option>
            <option value="approved">Goedgekeurd</option>
            <option value="rejected">Afgewezen</option>
          </select>
        </div>

        {loading ? (
          <div className="text-white/40 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Laden…</div>
        ) : filtered.length === 0 ? (
          <div className="bg-white/5 rounded-xl p-12 text-center text-white/40">Geen aanmeldingen.</div>
        ) : (
          <div className="space-y-3">
            {filtered.map((a) => (
              <div key={a.id} className="bg-white/5 rounded-xl p-5 border border-white/10" data-testid={`aff-row-${a.id}`}>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <h3 className="font-bold text-white">{a.full_name}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_COLORS[a.status]}`}>{a.status}</span>
                    </div>
                    <p className="text-white/60 text-xs">{a.email} {a.company_name && `· ${a.company_name}`}</p>
                    {a.website_or_channel && <p className="text-white/40 text-xs mt-1">Kanaal: <a href={a.website_or_channel} target="_blank" rel="noopener noreferrer" className="text-[#D4A017]">{a.website_or_channel}</a></p>}
                    {a.audience_size && <p className="text-white/40 text-xs">Audience: {a.audience_size}</p>}
                    {a.why_join && (
                      <details className="mt-2">
                        <summary className="text-white/50 text-xs cursor-pointer">Motivatie tonen</summary>
                        <p className="text-xs text-white/70 mt-1 whitespace-pre-line">{a.why_join}</p>
                      </details>
                    )}
                    {a.promo_code && (
                      <div className="mt-2 flex items-center gap-2">
                        <code className="bg-[#D4A017]/10 text-[#D4A017] px-2 py-0.5 rounded text-xs font-mono">{a.promo_code}</code>
                        <button onClick={() => { navigator.clipboard.writeText(a.promo_url); toast.success('URL gekopieerd'); }} className="text-xs text-[#D4A017] hover:underline flex items-center gap-1"><Copy size={10} /> URL</button>
                      </div>
                    )}
                  </div>
                  {a.status === 'pending' && (
                    <div className="flex items-center gap-2">
                      <button onClick={() => setApproving(a)} className="bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid={`approve-aff-${a.id}`}>
                        <CheckCircle size={12} /> Goedkeuren
                      </button>
                      <button onClick={() => reject(a.id)} className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-lg px-3 py-1.5 text-xs flex items-center gap-1.5" data-testid={`reject-aff-${a.id}`}>
                        <X size={12} /> Afwijzen
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {approving && <ApproveModal api={api} affiliate={approving} onClose={() => setApproving(null)} onDone={() => { setApproving(null); load(); }} />}
      </div>
    </div>
  );
}


function ApproveModal({ api, affiliate, onClose, onDone }) {
  const [password, setPassword] = useState(`AFF${Math.random().toString(36).slice(2, 10)}`);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  const approve = async () => {
    if (password.length < 8) {
      toast.error('Wachtwoord minimaal 8 tekens');
      return;
    }
    setSaving(true);
    try {
      const r = await api('post', `/api/admin/affiliates/${affiliate.id}/approve`, { initial_password: password, notes });
      toast.success(`Goedgekeurd — promo-code: ${r.data.promo_code}`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Mislukt');
    }
    setSaving(false);
  };

  return (
    <div onClick={onClose} className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div onClick={(e) => e.stopPropagation()} className="bg-[#0f172a] border border-white/10 rounded-2xl p-6 max-w-md w-full" data-testid="approve-aff-modal">
        <h2 className="text-lg font-bold mb-2" style={{ fontFamily: "'Sora', sans-serif" }}>Goedkeuren: {affiliate.full_name}</h2>
        <p className="text-white/60 text-xs mb-4">Er wordt een portal account aangemaakt met rol 'affiliate' + een unieke promo-code.</p>
        <div className="space-y-3">
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Initieel wachtwoord</span>
            <input data-testid="aff-initial-password" value={password} onChange={(e) => setPassword(e.target.value)} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white font-mono text-sm" />
            <p className="text-[10px] text-white/40 mt-1">Stuur dit zelf naar {affiliate.email} (we e-mailen niet automatisch)</p>
          </label>
          <label className="block">
            <span className="text-xs uppercase tracking-wider text-white/50 font-bold">Notities</span>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white" />
          </label>
          <button onClick={approve} disabled={saving} className="w-full bg-emerald-500 hover:bg-emerald-600 text-white font-bold py-2.5 rounded-xl flex items-center justify-center gap-2" data-testid="confirm-approve-aff">
            {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} Bevestig goedkeuring
          </button>
        </div>
      </div>
    </div>
  );
}
